import React, { useState } from "react";
import axios from "axios";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";
import { FilePlus2, Copy, Check, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CreateDossierDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [formKey, setFormKey] = useState(0);

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen);
        if (!nextOpen) setFormKey((key) => key + 1);
      }}
    >
      <DialogTrigger asChild>
        <Button variant="outline">
          <FilePlus2 className="h-4 w-4 mr-2" />
          Créer un dossier
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Nouveau dossier client</DialogTitle>
        </DialogHeader>
        <CreateDossierForm key={formKey} onCreated={onCreated} />
      </DialogContent>
    </Dialog>
  );
}

export function CreateDossierForm({ onCreated }) {
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ prenom: "", nom: "", email: "", tarif: "1000", mode: "full" });
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const valid = form.prenom && form.nom && form.email;

  const create = async () => {
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/formulaire/dossiers`, {
        prenom: form.prenom, nom: form.nom, email: form.email,
        tarif: Number(form.tarif), mode: form.mode,
      });
      const url = `${window.location.origin}${res.data.form_path}`;
      setResult({ url });
      toast.success("Dossier créé");
      onCreated && onCreated();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de la création du dossier");
    } finally {
      setSubmitting(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(result.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (_) {
      toast.error("Copie impossible, sélectionne le lien manuellement.");
    }
  };

  const reset = () => {
    setForm({ prenom: "", nom: "", email: "", tarif: "1000", mode: "full" });
    setResult(null);
    setCopied(false);
  };

  return !result ? (
    <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              À créer à réception du virement. Génère un lien de formulaire à envoyer au client.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Prénom"><Input value={form.prenom} onChange={(e) => set("prenom", e.target.value)} /></Field>
              <Field label="Nom"><Input value={form.nom} onChange={(e) => set("nom", e.target.value)} /></Field>
            </div>
            <Field label="Email"><Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} /></Field>

            <Field label="Offre">
              <RadioGroup value={form.tarif} onValueChange={(v) => set("tarif", v)} className="flex gap-4">
                <Opt id="t500" value="500" label="500 €" />
                <Opt id="t1000" value="1000" label="1000 €" />
              </RadioGroup>
            </Field>

            <Field label="Type de formulaire">
              <RadioGroup value={form.mode} onValueChange={(v) => set("mode", v)} className="flex flex-col gap-2">
                <Opt id="mfull" value="full" label="Complet (nouveau client)" />
                <Opt id="mlight" value="light" label="Allégé (texte déjà finalisé → BD + mini-site)" />
              </RadioGroup>
            </Field>

            <Button onClick={create} disabled={!valid || submitting} className="w-full">
              {submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Création…</> : "Créer le dossier + générer le lien"}
            </Button>
    </div>
  ) : (
    <div className="space-y-3">
            <p className="text-sm">Lien du formulaire à envoyer au client (valable 30 jours) :</p>
            <div className="flex gap-2">
              <Input readOnly value={result.url} className="text-xs" onFocus={(e) => e.target.select()} />
              <Button variant="outline" size="icon" onClick={copy}>
                {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>
            <Button variant="ghost" className="w-full" onClick={reset}>Créer un autre dossier</Button>
    </div>
  );
}

function Field({ label, children }) {
  return <div className="space-y-1.5"><Label className="text-sm">{label}</Label>{children}</div>;
}

function Opt({ id, value, label }) {
  return (
    <label htmlFor={id} className="flex items-center gap-2 cursor-pointer text-sm">
      <RadioGroupItem id={id} value={value} />
      {label}
    </label>
  );
}
